import { isFormDecisionRequired } from '@tf/shared';
import { Box, Button, Group, Icon, TFCard, Tooltip } from '@tf/ui';
import type { FormSegment } from '@tf/utils';
import type React from 'react';
import { ReviewDecisionButton } from '@/components/reviews';
import type { ExtendedReviewContainer, FormState } from '@/core/types';
import { UserCapabilities } from '@/core/types';
import { useCurrentUserCapabilityCheck } from '@/core/utils';

import { FormCommentsButton } from './FormCommentsButton';
import { FormStatusLabel } from './FormStatusLabel';

interface Props {
	state: FormState;
	review: ExtendedReviewContainer;
	isLoading: boolean;
	isDirty: boolean;
	graphId: number;
	segments: FormSegment[];
}

export const FormFooter: React.FC<Props> = ({ state, review, isLoading, isDirty, graphId, segments }) => {
	const canReviewAccounts = useCurrentUserCapabilityCheck(UserCapabilities.CAN_REVIEW_ACCOUNTS_DATA);
	const canPrepareAccountsData = useCurrentUserCapabilityCheck(UserCapabilities.CAN_PREPARE_ACCOUNTS_DATA);

	const isReadOnly = segments.every((s) => s.accessMode === 'VIEW');

	const isDecisionButtonVisible =
		canReviewAccounts && review.state !== 'DRAFT' && isFormDecisionRequired(segments, review);

	return (
		<TFCard innerPadding=".75rem 1rem">
			<Group
				gap="md"
				sx={{
					display: 'flex',
					flexWrap: 'nowrap',
					justifyContent: 'end',
					alignItems: 'center',
				}}
			>
				<Box sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}>
					<FormStatusLabel status={state.statuses} isDecisionRequired={isDecisionButtonVisible} />
				</Box>
				<FormCommentsButton size="md" formId={state.id} graphId={graphId} />
				{isDecisionButtonVisible ? (
					<ReviewDecisionButton segments={segments} graphId={graphId} reviewStatus={state?.statuses?.review || ''} />
				) : null}
				{canPrepareAccountsData && !isReadOnly && (
					<Tooltip label="All form data is saved" disabled={isDirty || isLoading} openDelay={500}>
						<Button
							loading={isLoading}
							disabled={!isDirty || isLoading}
							leftSection={<Icon.IconDeviceFloppy size={16} />}
							type="submit"
						>
							{isDirty ? 'Save' : 'Saved'}
						</Button>
					</Tooltip>
				)}
			</Group>
		</TFCard>
	);
};
